import { useState, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2, Mail, XCircle, Download } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useApp } from "@/context/AppContext";
import { getPaymentStatus, voucherUrl } from "@/lib/api";
import { euro } from "@/lib/format";

export default function PaymentSuccess() {
  const { t } = useApp();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | failed
  const [data, setData] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let timer;
    const poll = async () => {
      try {
        const res = await getPaymentStatus(sessionId);
        setData(res);
        if (res.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(res.payment_status) || attempts.current >= 6) {
          setState("failed");
          return;
        }
      } catch (err) {
        if (err?.response?.status === 404 || attempts.current >= 6) {
          setState("failed");
          return;
        }
      }
      attempts.current += 1;
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="min-h-screen overflow-x-hidden bg-sand">
      <Navbar solid />
      <div className="mx-auto flex min-h-[70vh] max-w-xl flex-col items-center justify-center px-5 py-24 text-center">
        {state === "polling" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="payment-polling">
            <Loader2 size={44} className="mx-auto animate-spin text-terracotta" />
            <p className="mt-5 font-serif text-2xl text-petrol">{t("detail.processing_payment")}</p>
          </motion.div>
        )}
        {state === "paid" && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} data-testid="payment-success" className="flex flex-col items-center">
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
              <Check size={40} strokeWidth={3} />
            </div>
            <h1 className="mt-6 font-serif text-4xl font-semibold text-petrol">{t("detail.payment_ok")}</h1>
            <h2 className="mt-2 font-serif text-2xl text-petrol">{t("detail.booking_done")}</h2>
            {data?.amount != null && <p className="mt-2 text-petrol/70">{euro(data.amount)}</p>}
            {data?.booking_ref && (
              <p className="mt-4 rounded-lg bg-petrol px-5 py-2.5 font-mono text-sm text-sand">
                {t("detail.booking_ref")}: {data.booking_ref}
              </p>
            )}
            <p className="mt-5 flex items-center gap-2 text-sm text-petrol/60">
              <Mail size={16} className="text-terracotta" />
              {data?.email_sent === false ? t("detail.email_pending_note") : t("detail.email_sent_note")}
            </p>
            <Link to="/" className="mt-7 rounded-none bg-terracotta px-7 py-3 font-semibold text-white transition-colors hover:bg-terracotta-hover">
              {t("detail.back_home")}
            </Link>
            {data?.booking_ref && (
              <a
                href={voucherUrl(data.booking_ref)}
                target="_blank"
                rel="noreferrer"
                data-testid="voucher-download"
                className="mt-3 flex items-center gap-2 rounded-none border border-sand-border px-6 py-2.5 text-sm font-semibold text-petrol transition-colors hover:border-terracotta hover:text-terracotta"
              >
                <Download size={15} /> {t("detail.download_voucher")}
              </a>
            )}
          </motion.div>
        )}
        {state === "failed" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="payment-failed" className="flex flex-col items-center">
            <XCircle size={44} className="text-terracotta" />
            <h1 className="mt-5 font-serif text-3xl text-petrol">{t("detail.payment_failed")}</h1>
            <Link to="/stays" className="mt-6 rounded-none border border-sand-border px-6 py-3 font-semibold text-petrol hover:border-terracotta">
              {t("detail.back_home")}
            </Link>
          </motion.div>
        )}
      </div>
      <Footer />
    </div>
  );
}
